import { useEffect, useRef, useState } from "react";
import { COULEURS } from "@hoa/protocol";
import {
  appel,
  dateCourte,
  depuisChamps,
  heure,
  jourCourt,
  jourLong,
  minutes,
  mmss,
  pluriel,
  versChampDate,
  versChampHeure,
  type Annonceur,
  type Compte,
  type Direct,
  type EvenementPasse,
  type EvenementVue,
  type Question,
  type ReglesEvenement,
  type TableVue,
} from "./api.js";
import { Apercu, agir, Croix, dire, Dialogue, Frise, Impression, Info, Interrupteur, Kpi, Pas, Point, Reglage, Retour, useEnregistrement, useLecture, Voix } from "./commun.js";
import { useRegie } from "./Regie.js";
import { ESPACES_HUMAINS } from "../espaces.js";
import { PlanDuStand } from "./plan.js";
import { ChoixDuree } from "./ville.js";
import { DetailSignalement, ListeSignalements } from "./signalements.js";
import { PageBilan } from "./bilan.js";
import { Fil, LegendeFil, type LigneFil } from "./fil.js";

/**
 * L'espace Régie (direction D) : les événements d'un compte. Un à la fois ;
 * des tables créées à la main ; les règles, les textes, la zone ; le direct ;
 * et, une fois fini, le bilan.
 */

export function PageRegie(): JSX.Element {
  const { route, evenement } = useRegie();
  let page: JSX.Element;
  const besoinEvenement = ["direct", "fil", "tables", "table", "plan", "parametres", "textes", "zone"].includes(route.page);
  if (besoinEvenement && !evenement) {
    page = <Evenements />;
  } else {
    switch (route.page) {
      case "direct":
        page = <EnDirect e={evenement!} />;
        break;
      case "fil":
        page = <FilEvenement e={evenement!} />;
        break;
      case "tables":
        page = <Tables e={evenement!} />;
        break;
      case "table":
        page = <EditeurTable e={evenement!} id={route.id ?? "nouvelle"} />;
        break;
      case "plan":
        page = <PlanDuStand e={evenement!} />;
        break;
      case "parametres":
        page = <Parametres e={evenement!} />;
        break;
      case "textes":
        page = <TextesEvenement e={evenement!} />;
        break;
      case "zone":
        page = <Zone e={evenement!} />;
        break;
      case "annonceur":
        page = <ProfilAnnonceur />;
        break;
      case "signalements":
        page = route.id ? <DetailSignalement espace="regie" id={route.id} /> : <ListeSignalements espace="regie" />;
        break;
      case "bilan":
        page = <PageBilan id={route.id ?? ""} onglet={route.onglet ?? "resume"} />;
        break;
      default:
        page = <Evenements />;
    }
  }
  return <div className="main">{page}</div>;
}

/** Envoyer un changement de l'événement en cours, puis relire. */
function useMajEvenement(): (corps: Record<string, unknown>, dit?: string) => Promise<boolean> {
  const { relireEvenements } = useRegie();
  return async (corps, dit = "Enregistré.") => {
    const ok = await agir(() => appel("/api/regie/evenements/courant", { method: "PATCH", corps }), dit);
    await relireEvenements();
    return ok;
  };
}

const plage = (e: { debut: number; fin: number }): string => `${jourCourt(e.debut)} · ${heure(e.debut)} – ${heure(e.fin)}`;

/* ------------------------------------------------------------------ */
/* Événements (D1)                                                     */
/* ------------------------------------------------------------------ */

/**
 * Terminer l'événement avant son heure de fin (ou l'annuler avant son
 * début) : les tables se ferment, l'événement passe dans « Passés » avec son
 * bilan.
 */
/** Supprimer un événement passé : bilan, réponses et messages gardés, définitivement. */
function DialogueSupprimer(props: { p: EvenementPasse; onFermer: () => void }): JSX.Element {
  const { relireEvenements } = useRegie();
  const p = props.p;
  return (
    <Dialogue titre={`Supprimer « ${p.nom} » ?`} etroit onFermer={props.onFermer}>
      <p style={{ margin: 0 }}>
        Son bilan, ses réponses et ses {p.messages.toLocaleString("fr-FR")} {pluriel(p.messages, "message", "messages")} sont effacés. On ne peut pas revenir en arrière.
      </p>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button type="button" className="btn q" onClick={props.onFermer}>
          Garder
        </button>
        <button
          type="button"
          className="btn x"
          onClick={() =>
            void agir(() => appel(`/api/regie/evenements/${encodeURIComponent(p.id)}`, { method: "DELETE" }), "Événement supprimé.").then(async (ok) => {
              if (!ok) return;
              props.onFermer();
              await relireEvenements();
            })
          }
        >
          Supprimer
        </button>
      </div>
    </Dialogue>
  );
}

function DialogueTerminer(props: { e: EvenementVue; onFermer: () => void }): JSX.Element {
  const { relireEvenements, aller } = useRegie();
  const avant = Date.now() < props.e.debut;
  const occupees = props.e.tables.filter((t) => t.membres > 0);
  const personnes = occupees.reduce((n, t) => n + t.membres, 0);
  return (
    <Dialogue titre={avant ? `Annuler « ${props.e.nom} » ?` : `Terminer « ${props.e.nom} » maintenant ?`} etroit onFermer={props.onFermer}>
      <p style={{ margin: 0 }}>
        {avant
          ? `Prévu ${jourLong(props.e.debut).toLowerCase()} à ${heure(props.e.debut)}, il n'ouvrira pas.`
          : `Prévu jusqu'à ${heure(props.e.fin)}, il se termine tout de suite.`}{" "}
        {personnes > 0
          ? `${personnes} ${pluriel(personnes, "personne est", "personnes sont")} à ${pluriel(occupees.length, "une table", `${occupees.length} tables`)} : ${pluriel(personnes, "elle voit", "elles voient")} « table fermée ».`
          : ""}
      </p>
      <p className="sub" style={{ margin: 0 }}>
        Il passe dans « Passés », avec son bilan. On ne peut pas le rouvrir.
      </p>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button type="button" className="btn q" onClick={props.onFermer}>
          Garder l'événement
        </button>
        <button
          type="button"
          className="btn x"
          onClick={() =>
            void agir(() => appel("/api/regie/evenements/courant/terminer", { method: "POST" }), avant ? "Événement annulé." : "Événement terminé.").then(async (ok) => {
              if (!ok) return;
              props.onFermer();
              await relireEvenements();
              aller({ espace: "regie", page: "evenements" });
            })
          }
        >
          {avant ? "Annuler l'événement" : "Terminer maintenant"}
        </button>
      </div>
    </Dialogue>
  );
}

function Evenements(): JSX.Element {
  const { evenement, passes, aller } = useRegie();
  const [creation, setCreation] = useState(false);
  const [terminer, setTerminer] = useState(false);
  const [supprimer, setSupprimer] = useState<EvenementPasse | null>(null);
  const direct = useLecture(() => (evenement ? appel<Direct>("/api/regie/evenements/courant/direct") : Promise.resolve(null)), 15_000, [evenement?.id]);
  const d = direct.donnees;
  const avant = evenement && Date.now() < evenement.debut;
  return (
    <>
      <div className="hd">
        <div>
          <h1>Événements</h1>
        </div>
        <span className="sp" />
        <button
          type="button"
          className={evenement ? "btn q" : "btn p"}
          disabled={!!evenement}
          title={evenement ? "Un événement à la fois." : undefined}
          onClick={() => setCreation(true)}
        >
          Créer un événement
        </button>
      </div>
      <h2 className="h2">En cours</h2>
      {evenement ? (
        <div className="carte" style={{ display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 280px", minWidth: 0 }}>
            <span style={{ display: "block", font: "700 24px/1.2 'Atkinson Hyperlegible', sans-serif" }}>{evenement.nom}</span>
            <span className="sub">
              {evenement.lieu.nom ? `${evenement.lieu.nom} · ` : ""}
              {jourLong(evenement.debut)} · {heure(evenement.debut)} – {heure(evenement.fin)}
            </span>
          </div>
          <div className="kpis" style={{ gridTemplateColumns: "repeat(3, minmax(90px, 1fr))", flex: "0 1 420px" }}>
            {ESPACES_HUMAINS ? <Kpi valeur={d?.aUneTable ?? 0} libelle="à une table" /> : <Kpi valeur={d?.presents ?? 0} libelle="présents" />}
            <Kpi valeur={evenement.tables.length} libelle={pluriel(evenement.tables.length, "table", "tables")} />
            <Kpi valeur={d?.reunies ?? 0} libelle={pluriel(d?.reunies ?? 0, "réunie", "réunies")} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "stretch" }}>
            <button type="button" className="btn p" onClick={() => aller({ espace: "regie", page: avant && evenement.tables.length === 0 ? "tables" : "direct" })}>
              {avant && evenement.tables.length === 0 ? "Préparer" : "Piloter"}
            </button>
            <button type="button" className="btn s x" onClick={() => setTerminer(true)}>
              {avant ? "Annuler…" : "Terminer maintenant…"}
            </button>
          </div>
        </div>
      ) : (
        <div className="vide">Aucun événement en cours.</div>
      )}
      <h2 className="h2">Passés</h2>
      {passes.length === 0 ? (
        <div className="vide">Aucun événement passé.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Événement</th>
              <th>Date</th>
              <th>Participants</th>
              <th>Tables</th>
              <th>Messages</th>
              <th>Données gardées jusqu'au</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {passes.map((p) => (
              <tr key={p.id}>
                <td>
                  <b>{p.nom}</b>
                </td>
                <td>{jourCourt(p.debut)}</td>
                <td>{p.participants}</td>
                <td>{p.tables}</td>
                <td>{p.messages.toLocaleString("fr-FR")}</td>
                <td>{p.gardeJusqua ? dateCourte(p.gardeJusqua) : "—"}</td>
                <td>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button type="button" className="btn s" onClick={() => aller({ espace: "regie", page: "bilan", id: p.id })}>
                      Bilan
                    </button>
                    <button type="button" className="btn s x" onClick={() => setSupprimer(p)}>
                      Supprimer
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {creation ? <DialogueCreation onFermer={() => setCreation(false)} /> : null}
      {terminer && evenement ? <DialogueTerminer e={evenement} onFermer={() => setTerminer(false)} /> : null}
      {supprimer ? <DialogueSupprimer p={supprimer} onFermer={() => setSupprimer(null)} /> : null}
    </>
  );
}

/** Le lieu d'un événement : un nom, une adresse, et surtout un point. */
function useLieu(initial: { nom: string; adresse: string; lat: number | null; lon: number | null }) {
  const [lieu, setLieu] = useState(initial);
  const [cherche, setCherche] = useState(false);
  const chercher = async (): Promise<boolean> => {
    if (lieu.adresse.trim().length < 3) {
      dire("Écrivez une adresse.", true);
      return false;
    }
    setCherche(true);
    try {
      const p = await appel<{ lat: number; lon: number }>("/api/regie/geocoder", { method: "POST", corps: { adresse: lieu.adresse } });
      setLieu((l) => ({ ...l, lat: p.lat, lon: p.lon }));
      dire("Adresse trouvée.");
      return true;
    } catch (e) {
      dire(e instanceof Error ? e.message : String(e), true);
      return false;
    } finally {
      setCherche(false);
    }
  };
  const maPosition = (): void => {
    if (!navigator.geolocation) {
      dire("Ce navigateur ne donne pas de position.", true);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setLieu((l) => ({ ...l, lat: p.coords.latitude, lon: p.coords.longitude }));
        dire("Position prise.");
      },
      () => dire("Position refusée ou indisponible.", true),
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  };
  return { lieu, setLieu, chercher, maPosition, cherche };
}

function DialogueCreation(props: { onFermer: () => void }): JSX.Element {
  const { relireEvenements, aller } = useRegie();
  const demain = Date.now();
  const [nom, setNom] = useState("");
  const [date, setDate] = useState(versChampDate(demain));
  const [debut, setDebut] = useState("18:30");
  const [fin, setFin] = useState("22:00");
  const { lieu, setLieu, chercher, maPosition, cherche } = useLieu({ nom: "", adresse: "", lat: null, lon: null });
  const [envoi, setEnvoi] = useState(false);

  const creer = async (): Promise<void> => {
    const d = depuisChamps(date, debut);
    let f = depuisChamps(date, fin);
    if (d === null || f === null) return dire("Date ou heure illisible.", true);
    if (f <= d) f += 86_400_000; // une soirée qui finit après minuit
    let { lat, lon } = lieu;
    if (lat === null && lieu.adresse.trim()) {
      try {
        const p = await appel<{ lat: number; lon: number }>("/api/regie/geocoder", { method: "POST", corps: { adresse: lieu.adresse } });
        lat = p.lat;
        lon = p.lon;
      } catch (e) {
        return dire(e instanceof Error ? e.message : String(e), true);
      }
    }
    // Sans adresse ni position : l'événement n'a pas de zone, on y entre par son QR code.
    setEnvoi(true);
    const ok = await agir(
      () =>
        appel("/api/regie/evenements", {
          method: "POST",
          corps: {
            nom: nom.trim() || "Nouvel événement",
            lieu: { nom: lieu.nom, adresse: lieu.adresse, lat, lon },
            debut: d,
            fin: f,
            // Espaces Humains : la marque se présente déjà, l'accueil va droit aux tables.
            ...(ESPACES_HUMAINS ? { textes: { accueil: "Choisis une table : chacune a son sujet et son heure." } } : {}),
          },
        }),
      "Événement créé.",
    );
    setEnvoi(false);
    await relireEvenements();
    if (ok) {
      props.onFermer();
      aller({ espace: "regie", page: "tables" });
    }
  };

  return (
    <Dialogue titre="Créer un événement" onFermer={props.onFermer}>
      <div className="champ">
        <label htmlFor="rg-e-nom">Nom</label>
        <input id="rg-e-nom" className="in" maxLength={80} value={nom} autoFocus onChange={(e) => setNom(e.target.value)} />
      </div>
      <div className="champ">
        <label htmlFor="rg-e-lieu">Lieu</label>
        <input id="rg-e-lieu" className="in" maxLength={80} value={lieu.nom} onChange={(e) => setLieu({ ...lieu, nom: e.target.value })} />
      </div>
      {ESPACES_HUMAINS ? null : (
        <>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
            <div className="champ" style={{ flex: "1 1 auto" }}>
              <label htmlFor="rg-e-adr">Adresse</label>
              <input id="rg-e-adr" className="in" maxLength={160} value={lieu.adresse} onChange={(e) => setLieu({ ...lieu, adresse: e.target.value, lat: null, lon: null })} />
            </div>
            <button type="button" className="btn q" disabled={cherche} onClick={() => void chercher()}>
              Chercher
            </button>
            <button type="button" className="btn q" onClick={maPosition}>
              Ma position
            </button>
          </div>
          <span className="sub">{lieu.lat !== null ? `Point posé : ${lieu.lat.toFixed(5)}, ${lieu.lon!.toFixed(5)}` : "Sans adresse : on entre par le QR code de l'événement."}</span>
        </>
      )}
      <div style={{ display: "flex", gap: 10 }}>
        <div className="champ" style={{ flex: "1 1 auto" }}>
          <label htmlFor="rg-e-date">Date</label>
          <input id="rg-e-date" className="in" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="champ" style={{ width: 130 }}>
          <label htmlFor="rg-e-deb">Début</label>
          <input id="rg-e-deb" className="in" type="time" value={debut} onChange={(e) => setDebut(e.target.value)} />
        </div>
        <div className="champ" style={{ width: 130 }}>
          <label htmlFor="rg-e-fin">Fin</label>
          <input id="rg-e-fin" className="in" type="time" value={fin} onChange={(e) => setFin(e.target.value)} />
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button type="button" className="btn q" onClick={props.onFermer}>
          Annuler
        </button>
        <button type="button" className="btn p" disabled={envoi} onClick={() => void creer()}>
          Créer
        </button>
      </div>
    </Dialogue>
  );
}

/* ------------------------------------------------------------------ */
/* Paramètres (D2, D11)                                                */
/* ------------------------------------------------------------------ */

function OngletsParametres(props: { actif: "parametres" | "textes" }): JSX.Element {
  const { aller } = useRegie();
  return (
    <nav className="chips" aria-label="Paramètres">
      {(
        [
          ["parametres", "Règles de l'événement"],
          ["textes", "Textes"],
        ] as const
      ).map(([p, l]) => (
        <button type="button" key={p} className={props.actif === p ? "chip on" : "chip"} aria-current={props.actif === p ? "page" : undefined} onClick={() => aller({ espace: "regie", page: p })}>
          {l}
        </button>
      ))}
    </nav>
  );
}

const DUREES_CHAT: Array<number | null> = [5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 240, 360, 480, 720, 1440, null];
const MESSAGES: Array<number | null> = [5, 10, 20, 30, 40, 60, 100, 200, 500, 1000, null];
const CONSERVATION = [1, 7, 14, 30, 60, 90, 180, 365];
const serie = (a: number, b: number, pas = 1): number[] => {
  const out: number[] = [];
  for (let v = a; v <= b; v += pas) out.push(v);
  return out;
};
const minutesLisibles = (v: number): string => (v < 60 ? `${v} min` : v % 60 === 0 ? `${v / 60} h` : `${Math.floor(v / 60)} h ${v % 60}`);

function Parametres(props: { e: EvenementVue }): JSX.Element {
  const e = props.e;
  const maj = useMajEvenement();
  const [regles, setRegles] = useState<ReglesEvenement>(e.regles);
  const [conservation, setConservation] = useState(e.conservationJours);
  const [prevenir, setPrevenir] = useState(e.prevenir);
  const [nom, setNom] = useState(e.nom);
  const [lieu, setLieu] = useState(e.lieu.nom);
  const [date, setDate] = useState(versChampDate(e.debut));
  const [debut, setDebut] = useState(versChampHeure(e.debut));
  const [fin, setFin] = useState(versChampHeure(e.fin));
  const { aller } = useRegie();

  const envoyerRegles = useEnregistrement<ReglesEvenement>((r) => maj({ regles: r }).then(() => undefined));
  const envoyerTexte = useEnregistrement<Record<string, unknown>>((c) => maj(c).then(() => undefined), 900);

  const majRegle = <K extends keyof ReglesEvenement>(k: K, v: ReglesEvenement[K]): void => {
    const s = { ...regles, [k]: v };
    if (k === "minimum" && (v as number) > s.maximum) s.maximum = v as number;
    if (k === "maximum" && (v as number) < s.minimum) s.minimum = v as number;
    setRegles(s);
    envoyerRegles(s);
  };
  const majHoraires = (d: string, h1: string, h2: string): void => {
    const a = depuisChamps(d, h1);
    let b = depuisChamps(d, h2);
    if (a === null || b === null) return;
    if (b <= a) b += 86_400_000;
    envoyerTexte({ debut: a, fin: b });
  };

  return (
    <>
      <div className="hd">
        <div>
          <h1>
            Paramètres
            <Info texte={`Pour ${e.nom} seulement. Enregistrés à chaque changement.`} />
          </h1>
        </div>
      </div>
      <OngletsParametres actif="parametres" />
      <section className="carte" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 14, alignItems: "end" }}>
        <div className="champ">
          <label htmlFor="rg-p-nom">Nom</label>
          <input
            id="rg-p-nom"
            className="in"
            maxLength={80}
            value={nom}
            onChange={(x) => {
              setNom(x.target.value);
              if (x.target.value.trim()) envoyerTexte({ nom: x.target.value });
            }}
          />
        </div>
        <div className="champ">
          <label htmlFor="rg-p-lieu">Lieu</label>
          <input
            id="rg-p-lieu"
            className="in"
            maxLength={80}
            value={lieu}
            onChange={(x) => {
              setLieu(x.target.value);
              envoyerTexte({ lieu: { ...e.lieu, nom: x.target.value } });
            }}
          />
        </div>
        <div className="champ">
          <span className="lb">Date et horaires</span>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              className="in"
              type="date"
              aria-label="Date"
              value={date}
              onChange={(x) => {
                setDate(x.target.value);
                majHoraires(x.target.value, debut, fin);
              }}
            />
            <input
              className="in"
              type="time"
              aria-label="Début"
              style={{ width: 140, flex: "0 0 140px" }}
              value={debut}
              onChange={(x) => {
                setDebut(x.target.value);
                majHoraires(date, x.target.value, fin);
              }}
            />
            <input
              className="in"
              type="time"
              aria-label="Fin"
              style={{ width: 140, flex: "0 0 140px" }}
              value={fin}
              onChange={(x) => {
                setFin(x.target.value);
                majHoraires(date, debut, x.target.value);
              }}
            />
          </div>
        </div>
        {ESPACES_HUMAINS ? null : (
          <div>
            <button type="button" className="btn q" onClick={() => aller({ espace: "regie", page: "zone" })}>
              Zone : {e.zoneM >= 1000 ? `${(e.zoneM / 1000).toLocaleString("fr-FR")} km` : `${e.zoneM} m`}
            </button>
          </div>
        )}
      </section>
      <div className="grille2">
        <section className="carte carteReg">
          <h2 className="h2">Tables</h2>
          <Reglage libelle="Minimum pour qu'une table parte">
            <Pas libelle="Minimum" valeurs={serie(2, 50)} valeur={regles.minimum} format={String} onChange={(v) => majRegle("minimum", v)} />
          </Reglage>
          <Reglage libelle="Maximum par table">
            <Pas libelle="Maximum" valeurs={serie(2, 50)} valeur={regles.maximum} format={String} onChange={(v) => majRegle("maximum", v)} />
          </Reglage>
        </section>
        <section className="carte carteReg">
          <h2 className="h2">Chat</h2>
          <Reglage libelle="Ouvert avant le rendez-vous">
            <Pas libelle="Ouverture du chat" valeurs={[1, 2, 3, 5, 10, 15, 20, 30, 45, 60]} valeur={regles.chatAvantMin} format={(v) => `${v} min`} onChange={(v) => majRegle("chatAvantMin", v)} />
          </Reglage>
          <Reglage libelle="Durée du chat">
            <Pas libelle="Durée du chat" valeurs={DUREES_CHAT} valeur={regles.dureeChatMin} format={(v) => (v === null ? "Infini" : minutesLisibles(v))} onChange={(v) => majRegle("dureeChatMin", v)} />
          </Reglage>
          <Reglage libelle="Messages par personne">
            <Pas libelle="Messages" valeurs={MESSAGES} valeur={regles.messagesParPersonne} format={(v) => (v === null ? "Illimité" : String(v))} onChange={(v) => majRegle("messagesParPersonne", v)} />
          </Reglage>
        </section>
        <section className="carte carteReg">
          <h2 className="h2">Sur place</h2>
          {/* Espaces Humains : on se retrouve à la table, sans contrôle de distance. */}
          {ESPACES_HUMAINS ? null : (
            <Reglage libelle="Rayon de retrouvailles">
              <Pas libelle="Rayon" valeurs={serie(10, 150, 5)} valeur={regles.rayonM} format={(v) => `${v} m`} onChange={(v) => majRegle("rayonM", v)} />
            </Reglage>
          )}
          <Reglage libelle="Place rendue après">
            <Pas libelle="Délai d'abandon" valeurs={serie(1, 60)} valeur={regles.delaiAbandonMin} format={(v) => `${v} min`} onChange={(v) => majRegle("delaiAbandonMin", v)} />
          </Reglage>
        </section>
        <section className="carte carteReg">
          <h2 className="h2">Données</h2>
          <Reglage libelle="Gardées après l'événement">
            <Pas
              libelle="Conservation"
              valeurs={CONSERVATION}
              valeur={conservation}
              format={(v) => `${v} ${pluriel(v, "jour", "jours")}`}
              onChange={(v) => {
                setConservation(v);
                envoyerTexte({ conservationJours: v });
              }}
            />
          </Reglage>
          <Reglage libelle="Prévenir les participants que les échanges sont gardés">
            <Interrupteur
              libelle="Prévenir les participants"
              valeur={prevenir}
              onChange={(v) => {
                setPrevenir(v);
                void maj({ prevenir: v });
              }}
            />
          </Reglage>
        </section>
      </div>
    </>
  );
}

function TextesEvenement(props: { e: EvenementVue }): JSX.Element {
  const e = props.e;
  const { compte } = useRegie();
  const maj = useMajEvenement();
  const [accueil, setAccueil] = useState(e.textes.accueil);
  const [donnees, setDonnees] = useState(e.textes.donnees);
  const envoyer = useEnregistrement<{ accueil: string; donnees: string }>((t) => maj({ textes: t }).then(() => undefined), 900);
  return (
    <>
      <div className="hd">
        <div>
          <h1>
            Paramètres
            <Info texte={`Pour ${e.nom} seulement. Enregistrés à chaque changement.`} />
          </h1>
        </div>
      </div>
      <OngletsParametres actif="textes" />
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr)", gap: 18, alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <section className="carte">
            <div className="champ">
              <label htmlFor="rg-t-acc">Texte d'accueil</label>
              <textarea
                id="rg-t-acc"
                className="in"
                rows={3}
                maxLength={280}
                style={{ height: 96 }}
                value={accueil}
                onChange={(x) => {
                  setAccueil(x.target.value);
                  envoyer({ accueil: x.target.value, donnees });
                }}
              />
              <span className="sub">{accueil.length} / 280</span>
            </div>
          </section>
          <section className="carte">
            <div className="champ">
              <label htmlFor="rg-t-don">Texte sur les données</label>
              <textarea
                id="rg-t-don"
                className="in"
                rows={2}
                maxLength={280}
                style={{ height: 76 }}
                value={donnees}
                onChange={(x) => {
                  setDonnees(x.target.value);
                  envoyer({ accueil, donnees: x.target.value });
                }}
              />
            </div>
          </section>
        </div>
        <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <span className="etiq">Ce que voit un participant en arrivant</span>
          <Apercu
            annonceur={compte.annonceur}
            nom
            suite={
              <>
                {e.prevenir ? <span className="sub">{donnees}</span> : null}
                <span className="btn p s" style={{ alignSelf: "flex-start" }}>
                  Voir les tables
                </span>
              </>
            }
          >
            {accueil}
          </Apercu>
        </section>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Tables (D6) et une table (D7)                                       */
/* ------------------------------------------------------------------ */

function Tables(props: { e: EvenementVue }): JSX.Element {
  const { aller } = useRegie();
  const e = props.e;
  const places = e.tables.reduce((n, t) => n + t.maximum, 0);
  return (
    <>
      <div className="hd">
        <div>
          <h1>Tables</h1>
          <div className="sub">
            {e.tables.length} {pluriel(e.tables.length, "table", "tables")} · {places} {pluriel(places, "place", "places")} au plus
          </div>
        </div>
        <span className="sp" />
        <button type="button" className="btn p" onClick={() => aller({ espace: "regie", page: "table", id: "nouvelle" })}>
          Créer une table
        </button>
      </div>
      {e.tables.length === 0 ? (
        <div className="vide">Aucune table.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Table</th>
              <th>Point de rencontre</th>
              <th>Places</th>
              <th>Rendez-vous</th>
              <th>Questions</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {e.tables
              .slice()
              .sort((a, b) => a.rdvAt - b.rdvAt || a.nom.localeCompare(b.nom, "fr", { numeric: true }))
              .map((t) => (
                <tr key={t.id} className="cliquable" onClick={() => aller({ espace: "regie", page: "table", id: t.id })}>
                  <td>
                    <span className="nom">
                      <Point couleur={t.couleur} />
                      <span>
                        <b>{t.nom}</b>
                        <small>{t.sujet}</small>
                      </span>
                    </span>
                  </td>
                  <td>{t.point || "—"}</td>
                  <td>
                    {t.minimum} – {t.maximum}
                  </td>
                  <td>{heure(t.rdvAt)}</td>
                  <td>{t.questions.length === 0 ? "Aucune" : `${t.questions.length} ${pluriel(t.questions.length, "question", "questions")}`}</td>
                  <td>
                    <button type="button" className="btn s q">
                      Modifier
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
    </>
  );
}

const questionVide = (i: number): Question => ({ id: `q${Date.now().toString(36)}${i}`, text: "", a: "", b: "", c: "" });

function EditeurTable(props: { e: EvenementVue; id: string }): JSX.Element {
  const { aller, relireEvenements, compte } = useRegie();
  const e = props.e;
  const existante = e.tables.find((t) => t.id === props.id) ?? null;
  const prises = new Set(e.tables.filter((t) => t.id !== existante?.id).map((t) => t.couleurId));
  const libre = COULEURS.find((c) => !prises.has(c.id))?.id ?? COULEURS[0]!.id;
  const rdvDefaut = Math.max(e.debut + 30 * 60_000, Math.ceil((Date.now() + (e.regles.chatAvantMin + 10) * 60_000) / (15 * 60_000)) * 15 * 60_000);

  const [nom, setNom] = useState(existante?.nom ?? `Table ${e.tables.length + 1}`);
  const [sujet, setSujet] = useState(existante?.sujet ?? "");
  const [point, setPoint] = useState(existante?.point ?? "");
  const [repere, setRepere] = useState(existante?.repere ?? "");
  const [rdv, setRdv] = useState(versChampHeure(existante?.rdvAt ?? Math.min(rdvDefaut, e.fin - 30 * 60_000)));
  const [couleur, setCouleur] = useState(existante?.couleurId ?? libre);
  const [min, setMin] = useState(existante?.minimum ?? e.regles.minimum);
  const [max, setMax] = useState(existante?.maximum ?? e.regles.maximum);
  const [avecQuestions, setAvecQuestions] = useState((existante?.questions.length ?? 0) > 0);
  const [questions, setQuestions] = useState<Question[]>(existante?.questions.length ? existante.questions : [questionVide(0)]);
  const [retrait, setRetrait] = useState(false);
  const [envoi, setEnvoi] = useState(false);

  const rdvAt = (): number | null => {
    let ms = depuisChamps(versChampDate(e.debut), rdv);
    if (ms === null) return null;
    if (ms < e.debut - 3_600_000 && ms + 86_400_000 <= e.fin + 3_600_000) ms += 86_400_000;
    return ms;
  };
  const completes = questions.filter((q) => q.text.trim() && q.a.trim() && q.b.trim() && q.c.trim());
  const apercuQ = completes[0] ?? questions[0];

  const enregistrer = async (): Promise<void> => {
    const r = rdvAt();
    if (r === null) return dire("Heure de rendez-vous illisible.", true);
    if (avecQuestions && completes.length !== questions.filter((q) => q.text.trim() || q.a.trim() || q.b.trim() || q.c.trim()).length) {
      return dire("Chaque question a besoin de ses trois réponses.", true);
    }
    setEnvoi(true);
    const corps = { nom, sujet, point, repere, rdvAt: r, couleurId: couleur, minimum: min, maximum: max, questions: avecQuestions ? completes : [] };
    const ok = await agir(
      () =>
        existante
          ? appel(`/api/regie/evenements/courant/tables/${existante.id}`, { method: "PATCH", corps })
          : appel("/api/regie/evenements/courant/tables", { method: "POST", corps }),
      existante ? "Table enregistrée." : "Table créée.",
    );
    setEnvoi(false);
    await relireEvenements();
    if (ok) aller({ espace: "regie", page: "tables" });
  };

  const majQ = (i: number, k: keyof Question, v: string): void => setQuestions((qs) => qs.map((q, j) => (j === i ? { ...q, [k]: v } : q)));

  return (
    <>
      <div className="hd">
        <button type="button" className="rond" aria-label="Retour aux tables" onClick={() => aller({ espace: "regie", page: "tables" })}>
          <Retour />
        </button>
        <h1>{existante ? nom || existante.nom : "Nouvelle table"}</h1>
        <span className="sp" />
        <button type="button" className="btn q" onClick={() => aller({ espace: "regie", page: "tables" })}>
          Annuler
        </button>
        <button type="button" className="btn p" disabled={envoi || !nom.trim()} onClick={() => void enregistrer()}>
          Enregistrer la table
        </button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.35fr)", gap: 18, alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="champ">
              <label htmlFor="rg-tb-nom">Nom</label>
              <input id="rg-tb-nom" className="in" maxLength={40} value={nom} onChange={(x) => setNom(x.target.value)} />
            </div>
            <div className="champ">
              <label htmlFor="rg-tb-sujet">Sujet</label>
              <input id="rg-tb-sujet" className="in" maxLength={80} value={sujet} onChange={(x) => setSujet(x.target.value)} />
            </div>
            <div className="champ">
              <label htmlFor="rg-tb-point">Point de rencontre</label>
              <input id="rg-tb-point" className="in" maxLength={80} value={point} onChange={(x) => setPoint(x.target.value)} />
            </div>
            <div className="champ">
              <label htmlFor="rg-tb-rep">Repère</label>
              <input id="rg-tb-rep" className="in" maxLength={120} value={repere} onChange={(x) => setRepere(x.target.value)} />
            </div>
            <div className="champ">
              <label htmlFor="rg-tb-rdv">Rendez-vous</label>
              <input id="rg-tb-rdv" className="in" type="time" value={rdv} onChange={(x) => setRdv(x.target.value)} style={{ width: 160 }} />
            </div>
            <div className="champ">
              <span className="lb">Couleur</span>
              <div className="nuancier">
                {COULEURS.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    className={prises.has(c.id) ? "prise" : ""}
                    style={{ background: c.color }}
                    aria-label={`${c.tribeName}${prises.has(c.id) ? " — prise" : ""}`}
                    title={`${c.tribeName}${prises.has(c.id) ? " — déjà prise par une autre table" : ""}`}
                    aria-pressed={couleur === c.id}
                    onClick={() => setCouleur(c.id)}
                  />
                ))}
              </div>
            </div>
          </section>
          <section className="carte carteReg">
            <Reglage libelle="Places au minimum">
              <Pas
                libelle="Minimum"
                valeurs={serie(2, 50)}
                valeur={min}
                format={String}
                onChange={(v) => {
                  setMin(v);
                  if (v > max) setMax(v);
                }}
              />
            </Reglage>
            <Reglage libelle="Places au maximum">
              <Pas
                libelle="Maximum"
                valeurs={serie(2, 50)}
                valeur={max}
                format={String}
                onChange={(v) => {
                  setMax(v);
                  if (v < min) setMin(v);
                }}
              />
            </Reglage>
          </section>
          {existante ? (
            <button type="button" className="btn x" style={{ alignSelf: "flex-start" }} onClick={() => setRetrait(true)}>
              Retirer la table…
            </button>
          ) : null}
        </div>
        <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h2 className="h2">Questions</h2>
            <span className="seg">
              <button type="button" className={avecQuestions ? "on" : ""} aria-pressed={avecQuestions} onClick={() => setAvecQuestions(true)}>
                Oui
              </button>
              <button type="button" className={!avecQuestions ? "on" : ""} aria-pressed={!avecQuestions} onClick={() => setAvecQuestions(false)}>
                Aucune
              </button>
            </span>
            <span className="sp" />
            {avecQuestions ? <span className="sub">{questions.length} sur 5</span> : null}
          </div>
          {avecQuestions ? (
            <>
              {questions.map((q, i) => (
                <div className="question" key={q.id}>
                  <span className="pill g">{i + 1}</span>
                  <input className="in" aria-label={`Question ${i + 1}`} placeholder="Question" maxLength={240} value={q.text} onChange={(x) => majQ(i, "text", x.target.value)} />
                  <input className="in" aria-label="Réponse 1" placeholder="Réponse 1" maxLength={120} value={q.a} onChange={(x) => majQ(i, "a", x.target.value)} />
                  <input className="in" aria-label="Réponse 2" placeholder="Réponse 2" maxLength={120} value={q.b} onChange={(x) => majQ(i, "b", x.target.value)} />
                  <input className="in" aria-label="Réponse 3" placeholder="Réponse 3" maxLength={120} value={q.c} onChange={(x) => majQ(i, "c", x.target.value)} />
                  <button type="button" className="rond" aria-label={`Retirer la question ${i + 1}`} onClick={() => setQuestions((qs) => (qs.length > 1 ? qs.filter((_, j) => j !== i) : [questionVide(0)]))}>
                    <Croix />
                  </button>
                </div>
              ))}
              {questions.length < 5 ? (
                <button type="button" className="btn s q" style={{ alignSelf: "flex-start" }} onClick={() => setQuestions((qs) => [...qs, questionVide(qs.length)])}>
                  Ajouter une question
                </button>
              ) : null}
              {apercuQ ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <span className="etiq">Ce que voit un participant</span>
                  <Apercu
                    annonceur={compte.annonceur}
                    suite={
                      <div className="reponsesApercu">
                        {[apercuQ.a, apercuQ.b, apercuQ.c].map((r, k) => (
                          <span key={k}>{r || `Réponse ${k + 1}`}</span>
                        ))}
                      </div>
                    }
                  >
                    <small>
                      Question 1 sur {Math.max(1, completes.length)}
                    </small>
                    {apercuQ.text || "…"}
                  </Apercu>
                </div>
              ) : null}
            </>
          ) : (
            <span className="sub">Aucune question.</span>
          )}
        </section>
      </div>
      {retrait && existante ? (
        <Dialogue titre={`Retirer ${existante.nom} ?`} etroit onFermer={() => setRetrait(false)}>
          <p style={{ margin: 0 }}>
            {existante.membres > 0 ? `Les ${existante.membres} personnes de la table la voient fermer. ` : ""}La table disparaît de l'événement.
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button type="button" className="btn q" onClick={() => setRetrait(false)}>
              Garder la table
            </button>
            <button
              type="button"
              className="btn x"
              onClick={() =>
                void agir(() => appel(`/api/regie/evenements/courant/tables/${existante.id}`, { method: "DELETE" }), "Table retirée.").then(async (ok) => {
                  await relireEvenements();
                  if (ok) aller({ espace: "regie", page: "tables" });
                })
              }
            >
              Retirer
            </button>
          </div>
        </Dialogue>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* En direct (D3)                                                      */
/* ------------------------------------------------------------------ */

function humainsTable(t: TableVue): string {
  if (t.phase === "inscriptions") return t.membres < t.minimum ? `${t.membres} / min. ${t.minimum}` : `${t.membres}`;
  if (t.phase === "rendez-vous" || t.phase === "réunie") return `${t.membres} · ${t.arrives} ${pluriel(t.arrives, "arrivé", "arrivés")}`;
  return `${t.membres}`;
}

function ensuiteTable(t: TableVue): JSX.Element | string {
  if (t.signalements > 0) return <span className="urg">{t.signalements} {pluriel(t.signalements, "signalement", "signalements")}</span>;
  if (t.phase === "inscriptions" && t.chatOuvreDansS !== null) {
    const txt = `chat dans ${minutes(t.chatOuvreDansS)}`;
    return t.chatOuvreDansS <= 600 && t.membres < t.minimum ? <span className="urg">{txt}</span> : txt;
  }
  if (t.chatSansFin) return "chat ouvert";
  if (t.chatFinitDansS !== null) return `fin du chat dans ${minutes(t.chatFinitDansS)}`;
  return "—";
}

export function ouvrirProjection(id: string): void {
  window.open(`/regie/projection?evt=${encodeURIComponent(id)}`, `hoa-projection-${id}`, "noopener");
}

function EnDirect(props: { e: EvenementVue }): JSX.Element {
  const { aller } = useRegie();
  const l = useLecture(() => appel<Direct>("/api/regie/evenements/courant/direct"), 5_000);
  const [tiroir, setTiroir] = useState<string | null>(null);
  const [parler, setParler] = useState(false);
  const [terminer, setTerminer] = useState(false);
  const d = l.donnees;
  const e = d?.evenement ?? props.e;
  const table = tiroir ? (e.tables.find((t) => t.id === tiroir) ?? null) : null;
  const commande = (tid: string, action: string, corps: Record<string, unknown>, dit: string): void =>
    void agir(() => appel(`/api/regie/evenements/courant/tables/${tid}/${action}`, { method: "POST", corps }), dit).then(l.relire);

  return (
    <>
      <div className="hd">
        <div>
          <h1>{e.nom}</h1>
          <div className="sub">
            {e.ouvert ? "En direct" : Date.now() < e.debut ? `Ouvre à ${heure(e.debut)}` : "Terminé"} · {e.lieu.nom || "Lieu à préciser"} · jusqu'à {heure(e.fin)}
          </div>
        </div>
        <span className="sp" />
        <span className="seg">
          <button type="button" className="on" aria-pressed="true">
            Tableau
          </button>
          <button type="button" onClick={() => aller({ espace: "regie", page: "fil" })}>
            Fil
          </button>
        </span>
        <button type="button" className="btn s" onClick={() => ouvrirProjection(e.id)}>
          Écran central
        </button>
        <button type="button" className="btn s" onClick={() => setParler(true)}>
          Parler aux participants
        </button>
        <button type="button" className="btn s x" onClick={() => setTerminer(true)}>
          {Date.now() < e.debut ? "Annuler…" : "Terminer maintenant…"}
        </button>
      </div>
      {terminer ? <DialogueTerminer e={e} onFermer={() => setTerminer(false)} /> : null}
      {l.erreur ? <p className="erreur">{l.erreur}</p> : null}
      <div className="kpis">
        {/* Espaces Humains : pas de zone, donc ni présents ni « sans table ». */}
        {ESPACES_HUMAINS ? null : <Kpi valeur={d?.presents ?? "—"} libelle="présents dans la zone" />}
        <Kpi valeur={d?.aUneTable ?? "—"} libelle="à une table" />
        {ESPACES_HUMAINS ? null : <Kpi valeur={d?.sansTable ?? "—"} libelle="sans table" />}
        <Kpi valeur={d?.reunies ?? "—"} libelle={pluriel(d?.reunies ?? 0, "table réunie", "tables réunies")} />
        <Kpi valeur={d?.signalements ?? "—"} libelle={pluriel(d?.signalements ?? 0, "signalement", "signalements")} alerte={(d?.signalements ?? 0) > 0} />
      </div>
      {d && d.alertes.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <h2 className="h2">
            À surveiller <span className="pill g">{d.alertes.length}</span>
          </h2>
          {d.alertes.map((a) => {
            const t = e.tables.find((x) => x.id === a.tableId);
            return (
              <div className="alerte" key={a.tableId}>
                <Point couleur={a.couleur} />
                <div className="tx">
                  <b>
                    {a.nom} · {a.sujet}
                  </b>
                  <span className="sub">
                    Chat dans {minutes(a.dansS)} · {a.membres} sur {a.minimum} minimum
                  </span>
                </div>
                <button type="button" className="btn s p" disabled={!t?.forcable} title={t?.forcable ? undefined : "Il faut au moins deux inscrits."} onClick={() => commande(a.tableId, "demarrer", {}, "Chat ouvert.")}>
                  Ouvrir le chat maintenant
                </button>
                <button type="button" className="btn s q" onClick={() => commande(a.tableId, "decaler", { minutes: 15 }, "Décalée de 15 min.")}>
                  Décaler de 15 min
                </button>
              </div>
            );
          })}
        </div>
      ) : null}
      {e.tables.length === 0 ? (
        <div className="vide">Aucune table : créez-en dans « Tables ».</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Table</th>
              <th>Rendez-vous</th>
              <th>Phase</th>
              <th>Humains</th>
              <th>Ensuite</th>
            </tr>
          </thead>
          <tbody>
            {e.tables
              .slice()
              .sort((a, b) => a.rdvAt - b.rdvAt || a.nom.localeCompare(b.nom, "fr", { numeric: true }))
              .map((t) => (
                <tr key={t.id} className="cliquable" onClick={() => setTiroir(t.id)}>
                  <td>
                    <span className="nom">
                      <Point couleur={t.couleur} />
                      <span>
                        <b>{t.nom}</b>
                        <small>{t.sujet}</small>
                      </span>
                    </span>
                  </td>
                  <td>
                    {t.point || "—"} · {heure(t.rdvAt)}
                  </td>
                  <td>
                    <Frise etape={t.phase} />
                  </td>
                  <td>{humainsTable(t)}</td>
                  <td>{ensuiteTable(t)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
      {table ? <TiroirTable t={table} e={e} onFermer={() => setTiroir(null)} commande={commande} /> : null}
      {parler ? <DialogueParler e={e} onFermer={() => setParler(false)} /> : null}
    </>
  );
}

/**
 * Le fil de la soirée d'un événement : une ligne par table, comme celui de
 * la ville. Le chat d'une table peut durer après le rendez-vous, ou toute la
 * soirée : sa barre va alors jusqu'à la fin de l'événement.
 */
function FilEvenement(props: { e: EvenementVue }): JSX.Element {
  const { aller } = useRegie();
  const l = useLecture(() => appel<Direct>("/api/regie/evenements/courant/direct"), 5_000);
  const [tiroir, setTiroir] = useState<string | null>(null);
  const d = l.donnees;
  const e = d?.evenement ?? props.e;
  const maintenant = d?.a ?? Date.now();
  const table = tiroir ? (e.tables.find((t) => t.id === tiroir) ?? null) : null;
  const commande = (tid: string, action: string, corps: Record<string, unknown>, dit: string): void =>
    void agir(() => appel(`/api/regie/evenements/courant/tables/${tid}/${action}`, { method: "POST", corps }), dit).then(l.relire);

  const finPrevue = (t: TableVue, chatA: number): number => {
    if (t.chatFinA !== undefined) return t.chatFinA ?? e.fin;
    if (t.chatSansFin || e.regles.dureeChatMin === null) return e.fin;
    return Math.max(t.rdvAt, chatA + e.regles.dureeChatMin * 60_000);
  };
  const groupes = new Map<string, LigneFil[]>();
  for (const t of e.tables.slice().sort((a, b) => a.rdvAt - b.rdvAt || a.nom.localeCompare(b.nom, "fr", { numeric: true }))) {
    const chatA = t.chatOuvertA ?? (t.chatOuvreDansS !== null ? maintenant + t.chatOuvreDansS * 1000 : t.rdvAt);
    const cle = t.point || "Sans point de rendez-vous";
    groupes.set(cle, [
      ...(groupes.get(cle) ?? []),
      {
        id: t.id,
        couleur: t.couleur,
        nom: t.nom,
        sous: humainsTable(t),
        ouverte: t.ouverteA ?? e.debut,
        chatA,
        chatOuvert: t.chatOuvertA != null,
        personne: t.membres === 0,
        finChat: Math.min(e.fin, finPrevue(t, chatA)),
        rdvAt: t.rdvAt,
        reunieA: t.reunie ? (t.reunieA ?? null) : null,
        menacee: t.phase === "inscriptions" && t.chatOuvreDansS !== null && t.chatOuvreDansS <= 600 && t.membres > 0 && t.membres < t.minimum,
        // L'heure des signalements d'une table n'est pas dans le direct : on les pose sur « maintenant ».
        signalements: Array.from({ length: t.signalements }, () => maintenant),
      },
    ]);
  }

  return (
    <>
      <div className="hd">
        <button type="button" className="rond" aria-label="Revenir au tableau" onClick={() => aller({ espace: "regie", page: "direct" })}>
          <Retour />
        </button>
        <div>
          <h1>
            {ESPACES_HUMAINS ? "Le fil de l'événement" : "Le fil de la soirée"}
            <Info texte="Une ligne par table, le trait blanc = maintenant. Toucher une table ouvre son détail." />
          </h1>
          <div className="sub">
            {e.nom} · de {heure(e.debut)} à {heure(e.fin)}
          </div>
        </div>
        <span className="sp" />
        <span className="seg">
          <button type="button" onClick={() => aller({ espace: "regie", page: "direct" })}>
            Tableau
          </button>
          <button type="button" className="on" aria-pressed="true">
            Fil
          </button>
        </span>
      </div>
      {l.erreur ? <p className="erreur">{l.erreur}</p> : null}
      <Fil
        groupes={[...groupes].map(([titre, lignes]) => ({ titre, lignes }))}
        maintenant={maintenant}
        debut={e.debut}
        fin={e.fin}
        vide="Aucune table : créez-en dans « Tables »."
        onLigne={setTiroir}
      />
      <LegendeFil />
      {table ? <TiroirTable t={table} e={e} onFermer={() => setTiroir(null)} commande={commande} /> : null}
    </>
  );
}

function TiroirTable(props: { t: TableVue; e: EvenementVue; onFermer: () => void; commande: (tid: string, action: string, corps: Record<string, unknown>, dit: string) => void }): JSX.Element {
  const t = props.t;
  const [cloture, setCloture] = useState(false);
  const [reunir, setReunir] = useState(false);
  const rang = t.phase === "inscriptions" ? 1 : t.phase === "chat" ? 2 : t.phase === "rendez-vous" ? 3 : t.phase === "réunie" ? 4 : 0;
  const c = props.commande;
  const etape = (n: number, titre: string, detail: string, actions?: JSX.Element): JSX.Element => (
    <>
      <div className="rail">
        <i className={n < rang ? "fait" : n === rang ? "courant" : ""} />
        {n < 4 ? <u className={n < rang ? "fait" : ""} /> : null}
      </div>
      <div className="corps" style={n === 4 ? { paddingBottom: 0 } : undefined}>
        <b className={n > rang ? "avenir" : ""}>{titre}</b>
        <div className="sub">{detail}</div>
        {actions && (n === rang || (n === 2 && rang > 2 && t.chatFinitDansS !== null)) ? <div className="actions">{actions}</div> : null}
      </div>
    </>
  );
  return (
    <>
      <div className="voile" onClick={props.onFermer} />
      <aside className="tiroir" aria-label={t.nom}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
          <span style={{ marginTop: 8 }}>
            <Point couleur={t.couleur} taille={22} />
          </span>
          <div style={{ flex: "1 1 auto" }}>
            <div style={{ font: "700 28px/1.15 'Atkinson Hyperlegible', sans-serif" }}>{t.nom}</div>
            <div className="sub">{t.sujet}</div>
          </div>
          <button type="button" className="rond" aria-label="Fermer" onClick={props.onFermer}>
            <Croix />
          </button>
        </div>
        <div className="sub" style={{ color: "#E5E5E5" }}>
          {t.point || "—"}
          {t.repere ? `, ${t.repere}` : ""} · rendez-vous {heure(t.rdvAt)}
        </div>
        <div className="kpis" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
          <Kpi valeur={t.membres} libelle={pluriel(t.membres, "membre", "membres")} />
          <Kpi valeur={t.arrives} libelle={pluriel(t.arrives, "arrivé", "arrivés")} />
          <Kpi valeur={t.signalements} libelle={pluriel(t.signalements, "signalement", "signalements")} />
        </div>
        <div className="etapes">
          {etape(
            1,
            "Inscriptions",
            `${t.membres} ${pluriel(t.membres, "inscrit", "inscrits")} · minimum ${t.minimum}, maximum ${t.maximum}`,
            <>
              <button type="button" className="btn s p" disabled={!t.forcable} onClick={() => c(t.id, "demarrer", {}, "Chat ouvert.")}>
                Ouvrir le chat maintenant
              </button>
              <button type="button" className="btn s q" onClick={() => c(t.id, "decaler", { minutes: 15 }, "Décalée de 15 min.")}>
                +15 min
              </button>
              <button type="button" className="btn s q" onClick={() => c(t.id, "decaler", { minutes: -15 }, "Avancée de 15 min.")}>
                −15 min
              </button>
            </>,
          )}
          {etape(
            2,
            rang === 2 ? "Chat · en cours" : "Chat",
            t.chatSansFin ? "Ouvert, sans heure de fin." : t.chatFinitDansS !== null ? `Se termine dans ${mmss(t.chatFinitDansS)}.` : rang < 2 && t.chatOuvreDansS !== null ? `S'ouvre dans ${minutes(t.chatOuvreDansS)}.` : "Fermé.",
            <>
              {rang === 2 ? (
                <button type="button" className="btn s p" onClick={() => c(t.id, "rendezvous", {}, "Rendez-vous maintenant.")}>
                  Passer au rendez-vous
                </button>
              ) : null}
              {t.chatSansFin ? null : (
                <>
                  <button type="button" className="btn s q" onClick={() => c(t.id, "chat", { minutes: 5 }, "Chat rallongé de 5 min.")}>
                    +5 min
                  </button>
                  <button type="button" className="btn s q" onClick={() => c(t.id, "chat", { minutes: -5 }, "Chat écourté de 5 min.")}>
                    −5 min
                  </button>
                </>
              )}
            </>,
          )}
          {etape(
            3,
            `Rendez-vous · ${heure(t.rdvAt)}`,
            `Arrivée validée à ${props.e.regles.rayonM} m.`,
            <button type="button" className="btn s p" disabled={t.membres < 2} onClick={() => setReunir(true)}>
              Réunir la table
            </button>,
          )}
          {etape(4, "Réunie", t.reunie ? "La table s'est retrouvée." : "Dès 2 arrivés.")}
        </div>
        {t.sessionId ? (
          <a className="lien" href={`/#/collectif?session=${encodeURIComponent(t.sessionId)}`} target="_blank" rel="noreferrer">
            Ouvrir l'écran collectif
          </a>
        ) : null}
        <div className="danger">
          <span className="etiq">Fin anticipée</span>
          <button type="button" className="btn x" style={{ alignSelf: "flex-start" }} onClick={() => setCloture(true)}>
            Clôturer la table…
          </button>
        </div>
      </aside>
      {reunir ? (
        <Dialogue titre={`Réunir ${t.nom} ?`} etroit onFermer={() => setReunir(false)}>
          <p style={{ margin: 0 }}>Chaque membre en route est compté arrivé, et les points communs partent.</p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button type="button" className="btn q" onClick={() => setReunir(false)}>
              Annuler
            </button>
            <button
              type="button"
              className="btn p"
              onClick={() => {
                setReunir(false);
                c(t.id, "reunir", {}, `${t.nom} réunie.`);
              }}
            >
              Réunir
            </button>
          </div>
        </Dialogue>
      ) : null}
      {cloture ? (
        <Dialogue titre={`Clôturer ${t.nom} ?`} etroit onFermer={() => setCloture(false)}>
          <p style={{ margin: 0 }}>{t.membres > 0 ? `Les ${t.membres} membres voient « table fermée » et retournent à la liste des tables.` : "Personne n'est encore à cette table."}</p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button type="button" className="btn q" onClick={() => setCloture(false)}>
              Garder la table
            </button>
            <button
              type="button"
              className="btn x"
              onClick={() => {
                c(t.id, "cloturer", {}, `${t.nom} clôturée.`);
                setCloture(false);
                props.onFermer();
              }}
            >
              Clôturer
            </button>
          </div>
        </Dialogue>
      ) : null}
    </>
  );
}

function DialogueParler(props: { e: EvenementVue; onFermer: () => void }): JSX.Element {
  const { compte } = useRegie();
  const [texte, setTexte] = useState("");
  const [dureeMs, setDureeMs] = useState(30 * 60_000);
  const [maintenant] = useState(Date.now());
  const nom = compte.annonceur.nom || (ESPACES_HUMAINS ? "La marque" : "Hoa");
  const n = props.e.tables.filter((t) => t.membres > 0).length;
  return (
    <Dialogue titre="Parler aux participants" onFermer={props.onFermer}>
      <div className="champ">
        <label htmlFor="rg-parler">{nom} dira cette phrase à tout l'événement</label>
        <textarea id="rg-parler" className="in" rows={2} maxLength={160} style={{ height: 84 }} value={texte} autoFocus onChange={(e) => setTexte(e.target.value)} />
        <span className="sub" style={{ alignSelf: "flex-end" }}>
          {texte.length} / 160
        </span>
      </div>
      <ChoixDuree maintenant={maintenant} onChange={setDureeMs} />
      <div className="champ">
        <span className="lb">Aperçu sur un téléphone</span>
        <Apercu annonceur={compte.annonceur} nom>
          {texte.trim() || "…"}
        </Apercu>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button type="button" className="btn q" onClick={props.onFermer}>
          Annuler
        </button>
        <button
          type="button"
          className="btn p"
          disabled={texte.trim().length < 2}
          onClick={() =>
            void agir(() => appel("/api/regie/evenements/courant/annonce", { method: "POST", corps: { texte: texte.trim(), dureeS: Math.round(dureeMs / 1000) } }), "Annonce affichée.").then((ok) => {
              if (ok) props.onFermer();
            })
          }
        >
          Afficher {n > 0 ? `sur ${n} ${pluriel(n, "table", "tables")} et l'accueil` : "à l'accueil de l'événement"}
        </button>
      </div>
    </Dialogue>
  );
}

/* ------------------------------------------------------------------ */
/* Zone et supports (D4)                                               */
/* ------------------------------------------------------------------ */

const ZONES = [30, 50, 75, 100, 150, 200, 300, 400, 500, 750, 1000, 1500, 2000];

function Zone(props: { e: EvenementVue }): JSX.Element {
  const e = props.e;
  const maj = useMajEvenement();
  const [zone, setZone] = useState(e.zoneM);
  const { lieu, setLieu, chercher, maPosition, cherche } = useLieu({ nom: e.lieu.nom, adresse: e.lieu.adresse, lat: e.lieu.lat, lon: e.lieu.lon });
  const envoyerZone = useEnregistrement<number>((z) => maj({ zoneM: z }).then(() => undefined));
  const [impression, setImpression] = useState<"affiche" | "chevalets" | null>(null);
  const premier = useRef(true);
  useEffect(() => {
    // Le point a changé (adresse trouvée, position prise) : on l'enregistre.
    if (premier.current) {
      premier.current = false;
      return;
    }
    if (lieu.lat !== null && lieu.lon !== null && (lieu.lat !== e.lieu.lat || lieu.lon !== e.lieu.lon)) {
      void maj({ lieu: { nom: lieu.nom, adresse: lieu.adresse, lat: lieu.lat, lon: lieu.lon } }, "Centre de la zone déplacé.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lieu.lat, lieu.lon]);
  const tables = e.tables.slice().sort((a, b) => a.nom.localeCompare(b.nom, "fr", { numeric: true }));
  const adresseSite = location.host;
  const { compte } = useRegie();
  const marque = (compte.annonceur.nom || (ESPACES_HUMAINS ? "Espaces Humains" : "Hoa")).toUpperCase();

  return (
    <>
      <div className="hd">
        <div>
          <h1>{ESPACES_HUMAINS ? "Écran et QR code" : "Zone et supports"}</h1>
        </div>
      </div>
      <div className="grille2">
        {/* Espaces Humains : on entre par le QR code, la zone ne sert pas. */}
        {ESPACES_HUMAINS ? null : (
        <div className="carte" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <b>
            Zone de l'événement
            <Info texte="À l'intérieur, la position d'un téléphone peut être imprécise : mieux vaut une zone large que des invités refusés." />
          </b>
          <Reglage libelle={`Rayon autour de ${e.lieu.nom || "l'adresse"}`}>
            <Pas
              libelle="Rayon de la zone"
              valeurs={ZONES}
              valeur={zone}
              format={(v) => (v >= 1000 ? `${(v / 1000).toLocaleString("fr-FR")} km` : `${v} m`)}
              onChange={(v) => {
                setZone(v);
                envoyerZone(v);
              }}
            />
          </Reglage>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
            <div className="champ" style={{ flex: "1 1 auto" }}>
              <label htmlFor="rg-z-adr">Centre</label>
              <input id="rg-z-adr" className="in" maxLength={160} value={lieu.adresse} onChange={(x) => setLieu({ ...lieu, adresse: x.target.value })} />
            </div>
            <button type="button" className="btn q s" disabled={cherche} onClick={() => void chercher()}>
              Chercher
            </button>
            <button type="button" className="btn q s" onClick={maPosition}>
              Ma position
            </button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="sub">Ouverte de</span>
            <span className="chip on">
              {heure(e.debut)} – {heure(e.fin)}
            </span>
            <span className="sub">{jourCourt(e.debut)}</span>
          </div>
        </div>
        )}
        <div className="carte" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h2 className="h2">Écran central</h2>
            <span className="sp" />
            <button type="button" className="btn s p" onClick={() => ouvrirProjection(e.id)}>
              Ouvrir en plein écran
            </button>
          </div>
          <ApercuEcran evenementId={e.id} />
        </div>
        <div className="carte" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <b>Affiche</b>
            <span className="sp" />
            <button type="button" className="btn s" onClick={() => setImpression("affiche")}>
              Imprimer
            </button>
          </div>
          <div className="papier" style={{ alignItems: "center", textAlign: "center" }}>
            <b style={{ letterSpacing: ".2em" }}>{marque}</b>
            <img src={`/api/evenements/${encodeURIComponent(e.id)}/qr.svg`} alt="" style={{ width: 96, height: 96 }} />
            <span style={{ font: "700 20px/1.15 'Atkinson Hyperlegible', sans-serif" }}>Scannez pour rejoindre une table.</span>
            <span style={{ color: "#555555" }}>
              {adresseSite} · {e.nom}
            </span>
          </div>
        </div>
        <div className="carte" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <b>Chevalets</b>
            <span className="sp" />
            <button type="button" className="btn s" disabled={tables.length === 0} onClick={() => setImpression("chevalets")}>
              Imprimer {tables.length > 1 ? `les ${tables.length}` : ""}
            </button>
          </div>
          <div className="papier" style={{ flexDirection: "row", alignItems: "center", gap: 14 }}>
            <span style={{ width: 40, height: 40, borderRadius: "50%", background: tables[0]?.couleur ?? "#4ba6ff", flex: "0 0 40px" }} />
            <div>
              <b style={{ display: "block", fontSize: 22 }}>{tables[0]?.nom ?? "Table 1"}</b>
              <span style={{ color: "#555555" }}>{e.nom}</span>
            </div>
          </div>
        </div>
      </div>
      {impression ? (
        <Impression onFini={() => setImpression(null)}>
          {impression === "affiche" ? (
            <div className="page">
              <div style={{ fontWeight: 700, letterSpacing: ".3em", fontSize: 28 }}>{marque}</div>
              <img src={`/api/evenements/${encodeURIComponent(e.id)}/qr.svg`} alt="" style={{ width: 420, height: 420 }} />
              <div style={{ fontWeight: 700, fontSize: 56, lineHeight: 1.1 }}>
                Un sujet, quelques personnes,
                <br />
                un rendez-vous ici.
              </div>
              <div style={{ fontSize: 26 }}>Scannez avec l'appareil photo · {adresseSite}/e/{e.id}</div>
              <div style={{ fontSize: 22, color: "#555555" }}>
                {e.nom} · {jourLong(e.debut)} · {heure(e.debut)} – {heure(e.fin)}
              </div>
            </div>
          ) : (
            tables.map((t) => (
              <div className="page" key={t.id}>
                <div style={{ width: 120, height: 120, borderRadius: "50%", background: t.couleur, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }} />
                <div style={{ fontWeight: 700, fontSize: 80 }}>{t.nom}</div>
                {t.sujet && t.sujet !== t.nom ? <div style={{ fontSize: 34 }}>{t.sujet}</div> : null}
                <div style={{ fontSize: 22, color: "#555555" }}>
                  {e.nom} · rendez-vous {heure(t.rdvAt)}
                </div>
                <img src={`/api/evenements/${encodeURIComponent(e.id)}/qr.svg`} alt="" style={{ width: 200, height: 200 }} />
                <div style={{ fontSize: 22 }}>Scannez pour rejoindre une table.</div>
              </div>
            ))
          )}
        </Impression>
      ) : null}
    </>
  );
}

/** L'écran central tel qu'il est, en réduction : la vraie page, à l'échelle de la carte. */
function ApercuEcran(props: { evenementId: string }): JSX.Element {
  const boite = useRef<HTMLDivElement>(null);
  const [k, setK] = useState(0.25);
  useEffect(() => {
    const el = boite.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setK(el.clientWidth / 1920));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={boite} className="ecranApercu">
      <iframe
        title="Aperçu de l'écran central"
        tabIndex={-1}
        src={`/regie/projection?evt=${encodeURIComponent(props.evenementId)}`}
        style={{ transform: `scale(${k})` }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Profil annonceur (D9)                                               */
/* ------------------------------------------------------------------ */

/** Une image réduite à 512 px de côté au plus : assez pour l'écran central. */
async function reduireImage(f: File): Promise<string> {
  const url = URL.createObjectURL(f);
  try {
    const img = await new Promise<HTMLImageElement>((ok, ko) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = () => ko(new Error("Image illisible."));
      i.src = url;
    });
    const cote = 512;
    const k = Math.min(1, cote / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(img.width * k));
    c.height = Math.max(1, Math.round(img.height * k));
    c.getContext("2d")!.drawImage(img, 0, 0, c.width, c.height);
    const png = c.toDataURL("image/png");
    return png.length < 180_000 ? png : c.toDataURL("image/jpeg", 0.86);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Quelques couleurs de départ ; « Autre… » ouvre le sélecteur. Le violet est celui d'Espaces Humains. */
const COULEURS_MARQUE = ["#7c5cff", "#111111", "#e5484d", "#f76b15", "#ffc53d", "#30a46c", "#0090ff", "#d6409f"];

function ProfilAnnonceur(): JSX.Element {
  const { compte, majCompte, evenement } = useRegie();
  const [a, setA] = useState<Annonceur>(compte.annonceur);
  const fichier = useRef<HTMLInputElement>(null);
  const exploitation = compte.id === "exploitation";
  const envoyer = useEnregistrement<Annonceur>(async (v) => {
    await agir(async () => {
      const r = await appel<{ compte: Compte }>("/api/regie/annonceur", { method: "PUT", corps: v });
      majCompte(r.compte);
    }, "Profil enregistré.");
  }, 800);
  const changer = (v: Annonceur): void => {
    setA(v);
    envoyer(v);
  };
  const premiereTable = evenement?.tables[0];
  return (
    <>
      <div className="hd">
        <div>
          <h1>
            Marque
            <Info
              texte={
                ESPACES_HUMAINS
                  ? "Le nom, le logo et la couleur de la marque : ils parlent aux visiteurs et habillent l'écran central."
                  : "Le nom, le logo et la couleur de la marque : ils parlent aux visiteurs à la place de Hoa, et habillent l'écran central."
              }
            />
          </h1>
        </div>
      </div>
      {exploitation ? <p className="erreur">L'entrée d'exploitation n'a pas de profil : connectez-vous avec un identifiant.</p> : null}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 1fr)", gap: 18, alignItems: "start" }}>
        <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Voix annonceur={a} taille={84} />
            <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
              <button type="button" className="btn s" disabled={exploitation} onClick={() => fichier.current?.click()}>
                Changer l'image
              </button>
              {a.image ? (
                <button type="button" className="lien" onClick={() => changer({ ...a, image: null })}>
                  Retirer l'image
                </button>
              ) : null}
              <input
                ref={fichier}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                hidden
                onChange={(x) => {
                  const f = x.target.files?.[0];
                  x.target.value = "";
                  if (f) void reduireImage(f).then((image) => changer({ ...a, image }), (e: Error) => dire(e.message, true));
                }}
              />
            </div>
          </div>
          <div className="champ">
            <label htmlFor="rg-a-nom">Nom affiché</label>
            <input id="rg-a-nom" className="in" maxLength={40} disabled={exploitation} value={a.nom} onChange={(x) => changer({ ...a, nom: x.target.value })} />
          </div>
          <div className="champ">
            <label htmlFor="rg-a-phrase">Petite phrase de présentation</label>
            <input id="rg-a-phrase" className="in" maxLength={140} disabled={exploitation} value={a.phrase} onChange={(x) => changer({ ...a, phrase: x.target.value })} />
          </div>
          <div className="champ">
            <span className="lb">Couleur de la marque</span>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
              {COULEURS_MARQUE.map((c) => (
                <button
                  key={c}
                  type="button"
                  className="pastille-couleur"
                  aria-label={c}
                  aria-pressed={(a.couleur || "") === c}
                  disabled={exploitation}
                  style={{ background: c }}
                  onClick={() => changer({ ...a, couleur: c })}
                />
              ))}
              <label className="btn s q" style={{ position: "relative", overflow: "hidden" }}>
                Autre…
                <input
                  type="color"
                  aria-label="Choisir une couleur"
                  disabled={exploitation}
                  value={a.couleur || "#7c5cff"}
                  style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }}
                  onChange={(x) => changer({ ...a, couleur: x.target.value })}
                />
              </label>
              {a.couleur ? (
                <span className="sub" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {a.couleur}
                </span>
              ) : null}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 12, borderTop: "1px solid #333333" }}>
            <Voix annonceur={null} taille={36} />
            <span className="sp" />
            <button type="button" className="btn s q" disabled={exploitation || (!a.nom && !a.image && !a.phrase && !a.couleur)} onClick={() => changer({ nom: "", image: null, phrase: "", couleur: "" })}>
              {ESPACES_HUMAINS ? "Tout effacer" : "Revenir à Hoa"}
            </button>
          </div>
        </section>
        <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <span className="etiq">Ce que voit un participant</span>
          <Apercu
            annonceur={a}
            nom
            suite={
              <span className="btn p s" style={{ alignSelf: "flex-start" }}>
                J'y vais
              </span>
            }
          >
            {a.phrase ? (
              <>
                {a.phrase}
                <br />
              </>
            ) : null}
            Bienvenue{evenement ? ` à ${evenement.nom}` : ""}. Choisis ta table :
            <br />
            <span style={{ fontSize: 18 }}>
              {premiereTable ? `${premiereTable.nom}${premiereTable.sujet && premiereTable.sujet !== premiereTable.nom ? ` · ${premiereTable.sujet}` : ""}` : "Table 1"}
            </span>
            {premiereTable?.point ? (
              <>
                <br />
                {premiereTable.point}, {heure(premiereTable.rdvAt)}.
              </>
            ) : null}
          </Apercu>
        </section>
      </div>
    </>
  );
}
